import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import useReveal from "@/hooks/useReveal";
import { PRIMARY_SHOPIFY_URL } from "@/lib/shopify";

const STATEMENTS = [
  {
    no: "I.",
    text: "We do not design accessories.",
    lower: "We design futures worn on the face.",
  },
  {
    no: "II.",
    text: "Identity is evolving.",
    lower: "Our objects evolve with it.",
  },
  {
    no: "III.",
    text: "Culture × Innovation.",
    lower: "One cannot lead without the other.",
  },
  {
    no: "IV.",
    text: "Wearable expression.",
    lower: "Quiet to the room. Loud to those who know.",
  },
  {
    no: "V.",
    text: "Slow is the new luxury.",
    lower: "We make less. We make it well. We number every piece.",
  },
];

function Statement({ no, text, lower }) {
  const r = useReveal();
  return (
    <div
      ref={r.ref}
      className={`reveal ${r.inView ? "in-view" : ""} relative border-t border-[#1f1f1f] py-14 md:py-20 grid grid-cols-12 gap-6 items-start`}
      data-testid={`vision-statement-${no.replace(".", "")}`}
    >
      <div className="col-span-2 md:col-span-2">
        <span className="font-editorial italic gold-text-gradient text-3xl md:text-4xl leading-none">
          {no}
        </span>
      </div>
      <div className="col-span-10 md:col-span-10">
        <p className="font-display text-[clamp(1.8rem,5vw,4rem)] leading-[1.05] tracking-[-0.02em] text-white">
          {text}
        </p>
        <p className="mt-4 font-editorial italic text-[#bdbdb7] text-[clamp(1.1rem,2.4vw,1.8rem)] leading-snug">
          {lower}
        </p>
      </div>
    </div>
  );
}

export default function VisionPage() {
  const r1 = useReveal();
  const r2 = useReveal();

  return (
    <div data-testid="vision-page" className="bg-[#070707] text-white">
      <Header />
      <main className="pt-28 md:pt-36 pb-24 relative overflow-hidden">
        {/* Ambient chrome glow */}
        <div
          aria-hidden
          className="absolute left-1/2 top-40 -translate-x-1/2 w-[820px] h-[820px] rounded-full pointer-events-none"
          style={{
            background:
              "radial-gradient(circle, rgba(212,175,55,0.12) 0%, rgba(192,192,192,0.05) 35%, transparent 70%)",
            filter: "blur(40px)",
          }}
        />

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
          {/* Hero */}
          <section
            ref={r1.ref}
            className={`reveal ${r1.inView ? "in-view" : ""} text-center py-12 md:py-20`}
          >
            <span className="font-label text-[#d4af37]">
              — The Manifesto · Vol. I —
            </span>
            <h1 className="mt-6 font-display text-[clamp(3rem,9vw,8rem)] leading-[0.92] tracking-[-0.035em] max-w-5xl mx-auto">
              The future speaks{" "}
              <span className="font-editorial italic gold-text-gradient">
                your language.
              </span>
            </h1>
            <p className="mt-10 max-w-xl mx-auto text-[#bdbdb7] text-lg leading-relaxed font-light">
              Not a brand. A frequency.
            </p>
          </section>

          {/* Statements */}
          <section className="mt-12 md:mt-20 max-w-5xl mx-auto">
            {STATEMENTS.map((s) => (
              <Statement key={s.no} {...s} />
            ))}
          </section>

          {/* Closing */}
          <section
            ref={r2.ref}
            className={`reveal ${r2.inView ? "in-view" : ""} mt-24 md:mt-32 text-center border-t border-[#1f1f1f] pt-20`}
          >
            <span className="font-label text-[#d4af37]">— Phase II · Loading —</span>
            <h2 className="mt-6 font-editorial italic text-[clamp(2.4rem,7vw,5.6rem)] leading-[1.02] gold-text-gradient max-w-4xl mx-auto">
              Adaptive Vision.
            </h2>
            <p className="mt-6 max-w-xl mx-auto text-[#a3a3a3] font-light leading-relaxed">
              Future Systems arrives in Phase II.
            </p>
            <div className="mt-10 flex items-center justify-center gap-4 flex-wrap">
              <a
                href={PRIMARY_SHOPIFY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="vision-shop-cta"
                className="btn-gold"
              >
                Preorder Edition 001
                <span aria-hidden>→</span>
              </a>
              <Link
                to="/journal"
                data-testid="vision-journal-cta"
                className="btn-ghost-gold"
              >
                Enter the Archive
              </Link>
            </div>
            <p className="mt-14 font-editorial italic text-[#cfcfca] text-xl">
              Designed for{" "}
              <span className="gold-text-gradient not-italic font-display">
                the culture.
              </span>
            </p>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}
